import { Layout } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  menuTabsConfig,
  PIXEL_INSTALLATION_KEY_PREFIX,
  restrictedRoutes,
} from 'components/PageLayout/constants';
import { PIXEL_INTEGRATIONS } from 'components/PixelInstallation/integrations';
import { createNavItem, MenuItem, NavItem, Sidebar, TabKeys } from 'components/Sidebar/Sidebar';
import { Menu } from 'components/common';

import { useAuth } from 'modules/auth/useAuth';
import { RoleKey, UserRoles } from 'modules/user/userApiTypes';

import { Route, ROUTES } from 'routing/constants';

import { ctvTwMerge } from 'services/twMerge/ctvTwMerge';

import { useOrganization } from '../../modules/organization/useOrganization';
import {
  DSP_ADFORM,
  DSP_AMAZON,
  DSP_CRITEO,
  DSP_FREEWHEEL,
  DSP_VISTAR,
  DSP_XANDR,
  supportsPixelConfig,
} from '../../utils/constants';
import { AccountMenu } from '../AccountMenu';
import { Header } from '../Header';
import { UserMenu } from '../UserMenu';

export type PageLayoutProps = {
  className?: string;
  contentClassName?: string;
  displaySidebar?: boolean;
  zeroContentPadding?: boolean;
  showSidebar?: boolean;
  showHeader?: boolean;
  children: ReactNode;
};

const findTabKeyByLink = (link: string): TabKeys | undefined => {
  if (link === ROUTES.HOME) {
    return TabKeys.DASHBOARD;
  }

  if (link === ROUTES.REPORTS) {
    return TabKeys.REPORT_BUILDER;
  }

  return (Object.keys(menuTabsConfig) as TabKeys[]).find((key) =>
    menuTabsConfig[key].some(
      (item) => item.linkTo === link || item.children?.some((child) => child.linkTo === link),
    ),
  );
};

export const PageLayout = ({
  className,
  contentClassName,
  showSidebar = true,
  showHeader = true,
  children,
}: PageLayoutProps) => {
  const [activeSidebarTab, setActiveSidebarTab] = useState<TabKeys>(TabKeys.DASHBOARD);
  const [selectedTabKey, setSelectedTabKey] = useState<number>(0);
  const navigate = useNavigate();
  const { dsp } = useOrganization();
  const prevDspRef = useRef(dsp);

  const pathName = window.location.pathname;
  const { search } = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const { role } = useAuth();
  //TODO CHANGE VARIABLE NAME OR HOW IT WORKS
  const hasLimitedAccess =
    role === UserRoles.LIMITED_ACCESS.value ||
    role === UserRoles.COOL_MEDIA_SELLER.value ||
    role === UserRoles.ADMIN.value;

  useEffect(() => {
    if (role && restrictedRoutes[role as RoleKey].includes(window.location.pathname as Route)) {
      navigate(ROUTES.HOME);
    }
  }, [role, navigate]);

  // Redirect DSPs without pixel config support from Pixel code to Legacy pixel code
  useEffect(() => {
    if (!supportsPixelConfig(dsp) && pathName === ROUTES.PIXEL_CODE) {
      navigate(ROUTES.PIXEL_LEGACY);
    }
  }, [dsp, pathName, navigate]);

  const getAvailableNavItems = (navItem: NavItem) => {
    const isRestricted = restrictedRoutes[role as RoleKey]?.includes(navItem?.linkTo as Route);

    const isVistar = dsp === DSP_VISTAR;
    const isXandr = dsp === DSP_XANDR;
    const isCriteo = dsp === DSP_CRITEO;
    const isAdform = dsp === DSP_ADFORM;
    const isAmazon = dsp === DSP_AMAZON;
    const isFreewheel = dsp === DSP_FREEWHEEL;

    // Filter out routes not supported for Vistar
    const isNotSupportedForVistar =
      navItem?.linkTo === ROUTES.XANDR_SETTINGS || navItem.linkTo === ROUTES.PUBLISHERS_SETTINGS;

    // Filter out routes not supported for Xandr
    const isNotSupportedForXandr = navItem?.linkTo === ROUTES.DSP_SETTINGS;

    // Filter out routes not supported for Criteo
    const isNotSupportedForCriteo =
      navItem?.linkTo === ROUTES.XANDR_SETTINGS || navItem.linkTo === ROUTES.PUBLISHERS_SETTINGS;

    // Filter out routes not supported for Adform
    const isNotSupportedForAdform = navItem?.linkTo === ROUTES.XANDR_SETTINGS;

    // Filter out routes not supported for Amazon
    const isNotSupportedForAmazon =
      navItem?.linkTo === ROUTES.XANDR_SETTINGS || navItem.linkTo === ROUTES.PUBLISHERS_SETTINGS;

    // Filter out routes not supported for FreeWheel.
    // Xandr connection is Xandr-specific. Publishers is hidden for now: FreeWheel
    // names the publisher on every impression, so the Adform-style mapping page
    // does not apply, and the read-only replacement is not in use yet.
    const isNotSupportedForFreewheel =
      navItem?.linkTo === ROUTES.XANDR_SETTINGS || navItem.linkTo === ROUTES.PUBLISHERS_SETTINGS;

    return (
      !isRestricted &&
      !(isVistar && isNotSupportedForVistar) &&
      !(isXandr && isNotSupportedForXandr) &&
      !(isCriteo && isNotSupportedForCriteo) &&
      !(isAdform && isNotSupportedForAdform) &&
      !(isAmazon && isNotSupportedForAmazon) &&
      !(isFreewheel && isNotSupportedForFreewheel)
    );
  };

  useEffect(() => {
    const activeTab = findTabKeyByLink(pathName);

    if (activeTab) {
      const activeNavItemIndex = menuTabsConfig[activeTab]
        .filter(getAvailableNavItems)
        .findIndex((navItem) => navItem.linkTo === pathName);

      setActiveSidebarTab(activeTab);
      setSelectedTabKey(activeNavItemIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  // Reset navbar selection to Agency when DSP changes
  useEffect(() => {
    if (prevDspRef.current !== dsp && activeSidebarTab === TabKeys.SETTINGS) {
      setSelectedTabKey(0);
      navigate(ROUTES.AGENCY_SETTINGS);
    }

    // When switching to a DSP with pixel config while on Legacy pixel code, go to Pixel code
    if (
      prevDspRef.current !== dsp &&
      supportsPixelConfig(dsp) &&
      pathName === ROUTES.PIXEL_LEGACY
    ) {
      setSelectedTabKey(0);
      navigate(ROUTES.PIXEL_CODE);
    }

    // When switching to a DSP without pixel config while on Pixel code, go to Legacy
    if (
      prevDspRef.current !== dsp &&
      supportsPixelConfig(prevDspRef.current) &&
      !supportsPixelConfig(dsp) &&
      pathName === ROUTES.PIXEL_CODE
    ) {
      setSelectedTabKey(0);
      navigate(ROUTES.PIXEL_LEGACY);
    }

    prevDspRef.current = dsp;
  }, [dsp, activeSidebarTab, navigate, pathName]);

  const getDynamicMenuConfig = () => {
    const baseConfig = [...menuTabsConfig[activeSidebarTab]];

    // Find and update the DSP connection label
    const dspConnectionIndex = baseConfig.findIndex((item) => item.linkTo === ROUTES.DSP_SETTINGS);

    if (dspConnectionIndex !== -1) {
      const dspLabel =
        dsp === DSP_VISTAR
          ? 'Vistar connection'
          : dsp === DSP_CRITEO
            ? 'Criteo connection'
            : dsp === DSP_ADFORM
              ? 'Adform connection'
              : dsp === DSP_AMAZON
                ? 'Amazon connection'
                : dsp === DSP_FREEWHEEL
                  ? 'FreeWheel connection'
                  : 'Dsp connection';

      baseConfig[dspConnectionIndex] = {
        ...baseConfig[dspConnectionIndex],
        label: dspLabel,
      };
    }

    // Disable "Pixel code" tab for DSPs with no pixel config support
    const pixelCodeIndex = baseConfig.findIndex((item) => item.linkTo === ROUTES.PIXEL_CODE);

    if (pixelCodeIndex !== -1 && !supportsPixelConfig(dsp)) {
      baseConfig[pixelCodeIndex] = {
        ...baseConfig[pixelCodeIndex],
        disabled: true,
      };
    }

    return baseConfig;
  };

  const availableNavItems = getDynamicMenuConfig().filter(getAvailableNavItems);
  const navItemsConfig: MenuItem[] = availableNavItems.map(createNavItem);

  // Installation guide children share one pathname; the platform comes from ?platform=.
  const isPixelInstallation = pathName === ROUTES.PIXEL_INSTALLATION;
  const installationGroupKey = String(availableNavItems.findIndex((item) => item.children));
  const requestedPlatform = new URLSearchParams(search).get('platform');
  const activePlatform =
    PIXEL_INTEGRATIONS.find((integration) => integration.id === requestedPlatform)?.id ??
    PIXEL_INTEGRATIONS[0].id;
  const selectedKeys = isPixelInstallation
    ? [`${PIXEL_INSTALLATION_KEY_PREFIX}${activePlatform}`]
    : [String(selectedTabKey)];

  // Auto-expand the Installation guide group while one of its pages is open
  useEffect(() => {
    if (isPixelInstallation) {
      setOpenKeys((keys) =>
        keys.includes(installationGroupKey) ? keys : [...keys, installationGroupKey],
      );
    }
  }, [isPixelInstallation, installationGroupKey]);

  const handleSidebarClick = (menuKey: TabKeys) => {
    setActiveSidebarTab(menuKey);
    setSelectedTabKey(0);
  };

  const hasExternalLink = (navItems: MenuItem[], key: string) =>
    navItems
      .filter((i) => String(i?.key).includes('external'))
      .map((i) => i?.key)
      .includes(key);

  const renderHeader = () =>
    showHeader ? (
      <Header>
        <AccountMenu />
        <UserMenu />
      </Header>
    ) : null;

  const renderContent = () => (
    <Content className="m-6 flex">
      {navItemsConfig.length > 1 && (
        <Menu
          mode="inline"
          // Sticky so only the page content scrolls; scrolls on its own if taller than the viewport
          className="layout-menu sticky top-6 max-h-[calc(100vh-3rem)] w-64 shrink-0 self-start overflow-y-auto overflow-x-hidden"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onSelect={(e) => {
            // Group children (Installation guide) are selected from the URL, not by index
            if (!hasExternalLink(navItemsConfig, e.key) && /^\d+$/.test(e.key)) {
              setSelectedTabKey(Number(e.key));
            }
          }}
          items={navItemsConfig}
        />
      )}
      {children}
    </Content>
  );

  return (
    <section className={classNames('page-background flex min-h-screen w-full', className)}>
      <section className="flex flex-1 justify-center">
        <div className={ctvTwMerge(classNames('flex-1'), contentClassName)}>
          {showSidebar ? (
            <Layout className="bg-secondary-gray">
              <div className="sidebar-container">
                <Sidebar
                  activeSidebarTab={activeSidebarTab}
                  hasLimitedAccess={hasLimitedAccess}
                  // defaultSettingsPage={ROUTES.MEMBERS_SETTINGS}
                  onChangeMenu={handleSidebarClick}
                />
              </div>
              <Layout className="bg-secondary-gray">
                {renderHeader()}
                {renderContent()}
              </Layout>
            </Layout>
          ) : (
            <Layout className="bg-secondary-gray">
              {renderHeader()}
              {renderContent()}
            </Layout>
          )}
        </div>
      </section>
    </section>
  );
};
